import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { WithOptionalProps } from "@b2bportal/core-types";
import {
  type DateRange,
  type GuestsSelection,
  LodgingSelectionEnum,
} from "@b2bportal/lodging-api";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import dayjs from "dayjs";
import {
  HotelsAvailabilityActions,
  HotelsAvailabilitySelectors,
} from "../../store/";
import type { HotelLocationSuggestion } from "../HotelLocationAutocomplete";

export const useHotelAvailabilitySearch = () => {
  const dispatch = useDispatch();

  const { t, formatDateTime } = useI18nContext();

  const dateRange = useSelector(
    HotelsAvailabilitySelectors.getSearchFormDateRange
  );
  const guests = useSelector(HotelsAvailabilitySelectors.getSearchFormGuests);
  const rooms = useSelector(
    HotelsAvailabilitySelectors.getSearchFormRoomsCount
  );
  const place = useSelector(HotelsAvailabilitySelectors.getSearchFormPlace);

  const [formDateRange, setFormDateRange] = useState<
    WithOptionalProps<DateRange, "from" | "until">
  >({});

  const search = useSelector(HotelsAvailabilitySelectors.getSearch);

  //Initial setup
  useEffect(() => {
    if (
      formDateRange.from == null &&
      formDateRange.until == null &&
      dateRange != null
    ) {
      setFormDateRange({ from: dateRange.from, until: dateRange.until });
    }
  }, [dateRange]);

  const handleDateRangeFromChange = (date: string | undefined) => {
    setFormDateRange({ from: date, until: undefined });
  };
  const handleDateRangeUntilChange = (date: string | undefined) => {
    setFormDateRange((prev) => ({ ...prev, until: date }));
  };

  const handlePlaceChange = (
    place: HotelLocationSuggestion | null,
    activeSearch = false
  ) => {
    if (place != null) {
      dispatch(HotelsAvailabilityActions.setNewPlace(place));
      if (search != null && activeSearch)
        dispatch(
          HotelsAvailabilityActions.setRequestedSearch({
            ...search,
            place,
            searchType: LodgingSelectionEnum.Place,
          })
        );
    }
  };
  const handleGuestsChange = (
    guests: GuestsSelection,
    roomCount?: number,
    activeSearch = false
  ) => {
    dispatch(HotelsAvailabilityActions.setNewGuests(guests));
    if (roomCount != null) {
      dispatch(HotelsAvailabilityActions.setNewRoomsCount(roomCount));
    }
    if (search != null && activeSearch)
      dispatch(
        HotelsAvailabilityActions.setRequestedSearch({
          ...search,
          guests,
          rooms: roomCount ?? rooms,
        })
      );
  };

  const handleDateRangeClose = (activeSearch = false) => {
    const { from, until } = formDateRange;
    if (
      from != null &&
      until != null &&
      dateRange &&
      (!dayjs(from).isSame(dateRange.from, "date") ||
        !dayjs(until).isSame(dateRange.until, "date"))
    ) {
      dispatch(HotelsAvailabilityActions.setNewDateRange({ from, until }));
      if (search != null && activeSearch)
        dispatch(
          HotelsAvailabilityActions.setRequestedSearch({
            ...search,
            dateRange: { from, until },
          })
        );
    } else {
      setFormDateRange(dateRange);
    }
  };

  const handleSearchClick = () => {
    if (
      search != null &&
      place != null &&
      dateRange.from != null &&
      dateRange.until != null
    ) {
      dispatch(
        HotelsAvailabilityActions.setRequestedSearch({
          ...search,
          place,
          guests,
          rooms,
          dateRange: { from: dateRange.from, until: dateRange.until },
          searchType: LodgingSelectionEnum.Place,
        })
      );
    }
  };

  return {
    context: {
      dateRange: formDateRange,
      guests,
      place,
      rooms,
    },
    handlers: {
      handleDateRangeFromChange,
      handleDateRangeUntilChange,
      handlePlaceChange,
      handleGuestsChange,
      handleDateRangeClose,
      handleSearchClick,
    },
    text: {
      locationLabel: t("core-hotels.search.location.label"),
      locationData: place?.label,
      searchSummary: t("core-hotels.search.summary", {
        fromDate: formatDateTime(
          dayjs(dateRange.from).toDate(),
          DateTimeFormatStyle.ShortMonthDayShortWeekday
        ),
        untilDate: formatDateTime(
          dayjs(dateRange.until).toDate(),
          DateTimeFormatStyle.ShortMonthDayShortWeekday
        ),
        count: guests.adults + guests.children.length,
      }),
      search: t("core-hotels.search.button"),
    },
  };
};
